import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BrowserRouter, Route, Routes, useParams } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { Button, Col, Modal, Row, Typography } from 'antd'

import { initializationState, langState } from 'src/data/common/commonAtoms'
import { PersistService } from 'src/data/common/PersistService'
import { orderDetailsSelector } from 'src/data/orders/OrderAtoms'
import SessionManager from 'src/managers/SessionManager'
import ShopifyManager from 'src/managers/ShopifyManager'
import DefaultLayout from 'src/modules/layouts/DefaultLayout'
import { getBrowserLang } from 'src/utils/helper'

import {
  accountDetails,
  accountPassword,
  addressesCreate,
  addressesCreateLinkedAccounts,
  addressesEdit,
  addressesEditLinkedAccounts,
  addressesList,
  balanceDue,
  home,
  logout,
  logoutRedirection,
  orderRMA,
  ordersDetails,
  ordersDetailsLinkedAccounts,
  ordersDetailsMissingPD,
  ordersDetailsMissingRx,
  ordersDetailsMissingRxOption,
  ordersList,
  ordersListLinkedAccounts,
  paymentSuccess,
  prescriptionDetails,
  prescriptionDetailsUpload,
  prescriptionDetailsUploadLinkedAccounts,
  prescriptionDetailsUploadOption,
  prescriptionDetailsUploadOptionLinkedAccounts,
  pupillaryDistanceDetails,
  pupillaryDistanceDetailsUpload,
  resetPassword,
  satisfactionSurveyRedirection,
  shopifyLogin,
  surveyMonkey,
} from './NamedRoutes'
import { ErrorFallbackModal } from 'src/modules/shared/ErrorFallbackModal'
import { ErrorBoundary } from 'react-error-boundary'

const Account = React.lazy(() => import('src/modules/account/Home'))
const ChangePassword = React.lazy(
  () => import('src/modules/account/PasswordResetLink')
)
const ResetPassword = React.lazy(
  () => import('src/modules/account/ResetPassword')
)
const Addresses = React.lazy(() => import('src/modules/addresses/Addresses'))
const AddressForm = React.lazy(
  () => import('src/modules/addresses/AddressForm')
)
const Orders = React.lazy(() => import('src/modules/orders/OrdersList'))
const Order = React.lazy(() => import('src/modules/orders/Details'))
const MissingRxOrder = React.lazy(
  () => import('src/modules/rx/uploadRx/ActionRx')
)
const RxOptionOrder = React.lazy(
  () => import('src/modules/rx/uploadRx/OptionRx')
)
const AddPdForm = React.lazy(() => import('src/modules/pd/uploadPd/AddPD'))
const BalanceDue = React.lazy(() => import('src/modules/orders/BalanceDue'))

const RMA = React.lazy(() => import('src/modules/orders/RMA'))
const RX = React.lazy(() => import('src/modules/rx/Details'))
const PD = React.lazy(() => import('src/modules/pd/Details'))
const PaymentSuccess = React.lazy(
  () => import('src/modules/orders/PaymentSuccess')
)
const ErrorPage = React.lazy(() => import('src/modules/layouts/ErrorPage'))

export const Loading = ({
  children,
}: {
  children: JSX.Element
}): JSX.Element => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
      {children}
    </ErrorBoundary>
  )
}

const Logout = () => {
  const urlToRedirect = logoutRedirection()
  PersistService.clearAll('local')
  window.location.href = urlToRedirect
  return null
}

const RedirectModal = () => {
  const { t } = useTranslation()
  const setLang = useSetRecoilState(langState)
  const lang = getBrowserLang()
  useEffect(() => {
    setLang(lang.indexOf('fr') > -1 ? 'fr' : 'en')
  }, [lang, setLang])

  return (
    <Modal
      open={true}
      footer={null}
      closable={false}
      className="redirect-modal"
    >
      <Row justify="center">
        <Col span={18}>
          <Row justify="center" gutter={[0, 20]}>
            <Col span={24}>
              <Typography.Text className="redirect-modal__text">
                {t('common:text.select_region')}
              </Typography.Text>
            </Col>
            <Col span={18}>
              <Button
                size="large"
                href={shopifyLogin('CA', 'fr')}
                className="redirect-modal__link"
              >
                CANADA - FR
              </Button>
            </Col>
            <Col span={18}>
              <Button
                size="large"
                href={shopifyLogin('CA', 'en')}
                className="redirect-modal__link"
              >
                CANADA - EN
              </Button>
            </Col>
            <Col span={22}>
              <Button
                size="large"
                href={shopifyLogin('US', 'en')}
                className="redirect-modal__link"
              >
                {t('common:text.usa').toUpperCase()}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Modal>
  )
}

const SatisfactionSurveyRedirect = () => {
  const { lang, orderId } = useParams()
  const order = useRecoilValue(orderDetailsSelector({orderId: orderId!, isChild: false}))

  window.location.href = surveyMonkey(orderId!, lang!, order.origin)
  return null
}

const GuardRoute = (): JSX.Element => {
  const isSettingsDone = useRecoilValue(initializationState)
  const isWithProxy =
    window.location.pathname.indexOf(process.env.REACT_APP_SHOPIFY_PROXY_URL!) >
    -1

  useEffect(() => {
    // Somehow an iframe is set on top of the view that's not in the code, preventing us from interacting with the app...
    const iframes = Array.from(document.body.children).filter(
      el => el.tagName.toLowerCase() === 'iframe'
    );
    const lastIframe = iframes[iframes.length - 1] as HTMLIFrameElement
    if (lastIframe) {
      lastIframe.style.zIndex = '0'
      lastIframe.style.display = 'none'
    }
  })
  return (
    <>
      {isWithProxy ? (
        <BrowserRouter basename={process.env.REACT_APP_SHOPIFY_PROXY_URL}>
          <Routes>
            <Route index element={<ShopifyManager />} />
          </Routes>
        </BrowserRouter>
      ) : (
        <BrowserRouter>
          <Routes>
            <Route index element={<SessionManager />} />
            <Route
              path={satisfactionSurveyRedirection}
              element={<SatisfactionSurveyRedirect />}
            />
            <Route path={resetPassword} element={
              <Loading>
                <ResetPassword />
              </Loading>} />
            <Route path={home} element={<DefaultLayout />}>
              <Route path={balanceDue} element={
                <Loading>
                  <BalanceDue />
                </Loading>} />
              <Route path={paymentSuccess} element={
                <Loading>
                  <PaymentSuccess />
                </Loading>
              } />
              <Route path={logout} element={<Logout />} />
            </Route>

            <Route
              path={home}
              element={isSettingsDone ? <DefaultLayout /> : <RedirectModal />}
            >
              <Route path={accountDetails} element={<Loading><Account /></Loading>} />
              <Route path={accountPassword} element={<Loading><ChangePassword /></Loading>} />
              <Route path={addressesList} element={
                <Loading>
                  <Addresses />
                </Loading>} />
              <Route path={addressesCreate} element={
                <Loading>
                  <AddressForm />
                </Loading>} />
              <Route path={addressesCreateLinkedAccounts} element={
                <Loading>
                  <AddressForm />
                </Loading>
              } />
              <Route path={addressesEdit} element={
                <Loading>
                  <AddressForm />
                </Loading>} />
              <Route path={addressesEditLinkedAccounts} element={
                <Loading>
                  <AddressForm />
                </Loading>} />
              <Route path={ordersList} element={
                <Loading>
                  <Orders />
                </Loading>} />
              <Route path={ordersListLinkedAccounts} element={
                <Loading>
                  <Orders />
                </Loading>} />
              <Route path={ordersDetailsLinkedAccounts} element={
                <Loading>
                  <Order />
                </Loading>
              } />
              <Route path={ordersDetails} element={
                <Loading>
                  <Order />
                </Loading>} />
              <Route
                path={ordersDetailsMissingRx}
                element={
                  <Loading>
                    <MissingRxOrder />
                  </Loading>
                }
              />
              <Route
                path={ordersDetailsMissingRxOption}
                element={
                  <Loading>
                    <RxOptionOrder />
                  </Loading>
                }
              />
              <Route
                path={prescriptionDetailsUpload}
                element={<Loading><MissingRxOrder /></Loading>}
              />
              <Route
                path={prescriptionDetailsUploadLinkedAccounts}
                element={<Loading><MissingRxOrder /></Loading>}
              />
              <Route
                path={prescriptionDetailsUploadOption}
                element={<Loading><RxOptionOrder /></Loading>}
              />
              <Route
                path={prescriptionDetailsUploadOptionLinkedAccounts}
                element={<Loading><RxOptionOrder /></Loading>}
              />
              <Route path={ordersDetailsMissingPD} element={<Loading><AddPdForm /></Loading>} />
              <Route
                path={pupillaryDistanceDetailsUpload}
                element={<Loading><AddPdForm /></Loading>}
              />
              <Route path={orderRMA} element={<Loading><RMA /></Loading>} />
              <Route path={prescriptionDetails} element={<Loading><RX /></Loading>} />
              <Route path={pupillaryDistanceDetails} element={<Loading><PD /></Loading>} />
              <Route path="*" element={<ErrorPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      )}
    </>
  )
}

export default GuardRoute
